import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { BankAccount, CheckIbanSRRequest, CheckIbanSRResponse, GenericResponse } from '@interfaces';
import { from, Subject } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';
import { CancelContractService } from 'src/app/services/cancel-contract.service';
import { NewContractPymesService } from 'src/app/services/new-contract-pymes.service';
import { UtilService } from 'src/app/services/util.service';
import { ModalCancelContractComponent } from 'src/app/shared/modal-cancel-contract/modal-cancel-contract.component';
import { ModalErrorBankAccountComponent } from 'src/app/shared/modal-error-bank-account/modal-error-bank-account.component';
import { HeaderService } from 'src/app/shared/ui/header/header.service';
import { ToastrService } from 'src/app/shared/ui/toastr/toastr.service';
import { ConfirmDialogComponent } from '../new-contract-pymes/confirm-dialog/confirm-dialog.component';
import { MultipointModalErrorInstallationComponent } from './components/multipoint-modal-error-installation/multipoint-modal-error-installation.component';
import { MultipointModalHaveInstallationsComponent } from './components/multipoint-modal-have-installations/multipoint-modal-have-installations.component';
import { MultipointModalResumeComponent } from './components/multipoint-modal-resume/multipoint-modal-resume.component';
import {
  BusinessMultiPoint,
  CreateContractMultipoint,
  GetDraftMultiPoint,
  InstallationMultiPoint,
  InstallationsMultiPoint,
  MultipointEnterMargins,
  PreviousInstallation,
  SavedDataMultipoint,
  SaveDraftMultiPoint,
  SignerMultiPoint,
  SocialReasonMultiPoint
} from './new-contract-pymes-multipoint.model';
import { NewContractPymesMultipointService } from './new-contract-pymes-multipoint.service';
import { AuthService } from 'src/app/auth/auth.service';

@Component({
  selector: 'app-new-contract-pymes-multipoint',
  templateUrl: './new-contract-pymes-multipoint.component.html',
  styleUrls: ['./new-contract-pymes-multipoint.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NewContractPymesMultipointComponent implements OnInit, OnDestroy, AfterContentChecked {
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public isLoading = false;
  public tabIndex = 1;
  public canSave = false;
  public canGoNext = false;
  public progress = 0;
  public chargedData = false;
  public fromDraft = false;
  private businessData: BusinessMultiPoint;
  public installationsData: InstallationMultiPoint[] = [];
  private preInstallationData: PreviousInstallation;
  private signerData: SignerMultiPoint;
  public progressInstallations = 0;
  public showProgressInstallations = false;
  public overWriteInstallations = true;
  public haveIdDraft = false;
  private isUserSpanish = true;

  constructor(
    private cdRef: ChangeDetectorRef,
    private activeRoute: ActivatedRoute,
    private newContractPymesMultipointService: NewContractPymesMultipointService,
    private utilService: UtilService,
    private toastrService: ToastrService,
    private dialog: MatDialog,
    private headerService: HeaderService,
    private newContractPymesService: NewContractPymesService,
    private cancelContractService: CancelContractService,
    private location: Location,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.isUserSpanish = this.authService.userData.data.country === 'ES';
    this.newContractPymesMultipointService.resetLoaderInstallations();
    this.newContractPymesMultipointService.resetSavedDataMultipoint();
    this.newContractPymesService.resetSavedData();
    this.getParamsUrl();
    this.subscribeIdDraft();
  }

  ngAfterContentChecked(): void {
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private subscribeIdDraft(): void {
    this.newContractPymesMultipointService.getSavedDataMultipoint().pipe(takeUntil(this.destroyed$)).subscribe(
      (response: SavedDataMultipoint) => {
        if (response !== null && response.idDraft) {
          this.haveIdDraft = true;
        }
      }
    );
  }

  private getParamsUrl(): void {
    this.activeRoute.queryParams.pipe(takeUntil(this.destroyed$)).subscribe((res: Params) => {
      if (res.crmContact) {
        this.newContractPymesMultipointService.setSavedDataMultipoint({ contactCRM: res.crmContact });
        this.chargedData = true;
        this.fromDraft = true;
        this.isLoading = false;
      }
      if (res.idDraft) {
        this.fromDraft = true;
        this.getDraft(res.idDraft);
      }
      if (res.codContract) {
        this.tabIndex = 5;
        this.newContractPymesMultipointService.setSavedDataMultipoint({ idContract: res.codContract });
        this.newContractPymesService.setSavedData({
          codigoContrato: res.codContract,
          installation: {
            codigoRazonSocial: res.codeSocialReason
          },
          business: {
            nombre: res.codeSocialReason
          },
          disabledAddSepa: res.disabledAddSepa,
          bankAccountsSepa: res.bankAccountsSepa,
          needFiles: true
        });
        this.chargedData = true;
        this.isLoading = false;
      }
    });
  }

  private getDraft(idDraft: string): void {
    this.isLoading = true;
    this.newContractPymesMultipointService.getDraftMultiPoint(idDraft).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse<GetDraftMultiPoint>) => {
        if (response.success) {
          this.tabIndex = response.data.numFormulario;
          this.canSave = true;
          this.mapperGetDraft(response.data, idDraft);
          this.chargedData = true;
        } else {
          this.utilService.toastErrors(response);
          this.handleGoBack();
        }
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
        this.utilService.toastErrors(error);
        this.handleGoBack();
      }
    );
  }

  private mapperGetDraft(response: GetDraftMultiPoint, idDraft: string): void {
    this.businessData = this.newContractPymesMultipointService.mapperGetBusinessDraft(response);
    this.preInstallationData = this.newContractPymesMultipointService.mapperGetPreInstallationDraft(response);
    this.installationsData = this.newContractPymesMultipointService.mapperGetInstallationDraft(response);
    this.signerData = this.newContractPymesMultipointService.mapperGetSignerDraft(response);
    this.newContractPymesMultipointService.setSavedDataMultipoint({
      idContract: null,
      idDraft,
      contactCRM: response.codigoUserCRM,
      approved: response.aprobadoCif,
      business: this.newContractPymesMultipointService.mapperGetBusinessDraft(response),
      previousInstallation: this.newContractPymesMultipointService.mapperGetPreInstallationDraft(response),
      installations: this.newContractPymesMultipointService.mapperGetInstallationDraft(response),
      signer: this.newContractPymesMultipointService.mapperGetSignerDraft(response)
    });
  }

  public changeLoader(loader: boolean): void {
    this.isLoading = loader;
  }

  public handleGoBack(): void {
    this.location.back();
  }

  public handleDownload(): void {
    this.isLoading = true;
    this.toastrService.error('COMMONS.DOWNLOADING');
    this.newContractPymesMultipointService.downloadTemplates(false).pipe(takeUntil(this.destroyed$)).subscribe(
      result => {
        if (result.body.type !== 'application/json') {
          const header = result.headers.get('Content-Disposition');
          const fileName = header.split(';')[1].split('=')[1].replace(/\"/g, '');
          const link = document.createElement('a');
          link.download = fileName;
          link.href = URL.createObjectURL(result.body);
          link.click();
          this.toastrService.success('COMMONS.DOWNLOADING_SUCCESS');
          this.isLoading = false;
        } else {
          const reader = new FileReader();
          reader.addEventListener('loadend', e => {
            this.toastrService.error(JSON.parse(e.target.result as string).Message);
            this.isLoading = false;
          });
          reader.readAsText(result.body);
        }
      },
      error => {
        this.isLoading = false;
        this.utilService.toastErrors(error);
      }
    );
  }

  public handleOpenProgress(): void {
    if (this.tabIndex !== 1) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        autoFocus: false,
        disableClose: true,
        height: window.screen.height,
        width: '472px',
        position: {
          right: '0px'
        },
        data: this.tabIndex
      };
      this.dialog.open(MultipointModalResumeComponent, dialogConfig.data);
    }
  }

  public handleSave(tabIndex?: number, installations?: boolean, message = true, openModal = false): void {
    this.isLoading = true;
    this.newContractPymesMultipointService.getSavedDataMultipoint().pipe(takeUntil(this.destroyed$)).subscribe(
      response => {
        this.destroyed$.next();
        this.newContractPymesMultipointService.saveDraftMultiPoint(response.idDraft, this.mapperSaveParams(response, tabIndex))
          .pipe(takeUntil(this.destroyed$)).subscribe(
            res => {
              if (res.success) {
                if (tabIndex) {
                  this.tabIndex = tabIndex;
                  if (tabIndex === 3) {
                    this.newContractPymesMultipointService.setSavedDataMultipoint({ previousInstallation: this.preInstallationData });
                    if (this.overWriteInstallations) {
                      this.newContractPymesMultipointService.setSavedDataMultipoint({
                        installations: this.preInstallationData.installations
                      });
                    }
                  } else {
                    this.newContractPymesMultipointService.setSavedDataMultipoint({ installations: this.installationsData });
                  }
                  this.isLoading = false;
                } else if (installations && !openModal) {
                  this.newContractPymesMultipointService.setSavedDataMultipoint({ installations: this.installationsData });
                  this.postInstallations();
                } else {
                  this.saveData();
                  if (message) {
                    this.toastrService.error('NEW_CONTRACTS.DRAFT_SAVED');
                  }
                  this.isLoading = false;
                }
                if (!this.isUserSpanish && openModal) {
                  this.confirmModal();
                }
              } else {
                this.isLoading = false;
                this.utilService.toastErrors(res);
              }
            },
            error => {
              this.isLoading = false;
              this.utilService.toastErrors(error);
            }
          );
      }
    );
  }

  private saveData(): void {
    switch (this.tabIndex) {
      case 1:
        this.newContractPymesMultipointService.setSavedDataMultipoint({ business: this.businessData });
        break;
      case 2:
        this.newContractPymesMultipointService.setSavedDataMultipoint({ previousInstallation: this.preInstallationData });
        break;
      case 3:
        this.newContractPymesMultipointService.setSavedDataMultipoint({ installations: this.installationsData });
        break;
      case 4:
        this.newContractPymesMultipointService.setSavedDataMultipoint({ signer: this.signerData });
        break;
    }
  }

  private mapperSaveParams(values: SavedDataMultipoint, tabIndex?: number): SaveDraftMultiPoint {
    return {
      idBorrador: Number(values.idDraft),
      numFormulario: tabIndex || Number(this.tabIndex),
      aprobadoCif: values.approved,
      infoRazonSocial: {
        codigoRazonSocial: this.businessData?.socialReason ? this.businessData?.socialReason?.toString() : null,
        editable: this.businessData?.canEdit || false,
        nombre: this.businessData?.socialReasonLabel || null,
        cif: this.businessData?.numberDocument || null,
        cnae: this.businessData?.cnae || null,
        nombreContactoComercial: this.businessData?.contactName || null,
        apellidosContactoComercial: this.businessData?.surname || null,
        dniContactoComercial: this.businessData?.dni || null,
        telefonoContactoComercial: this.businessData?.mobile || null,
        mailContactoComercial: this.businessData?.email || null,
        nombreTipoViaDireccion: this.businessData?.roadType || null,
        nombreDireccion: this.businessData?.roadName || null,
        numeroDireccion: this.businessData?.addressNumber || null,
        portalDireccion: this.businessData?.gate || null,
        escaleraDireccion: this.businessData?.stair || null,
        pisoDireccion: this.businessData?.floor || null,
        puertaDireccion: this.businessData?.door || null,
        codPostalDireccion: this.businessData?.postalCode || null,
        codigoPoblacionDireccion: this.businessData?.town || null,
        codigoProvinciaDireccion: this.businessData?.province || null
      },
      infoPrevioInstalacion: {
        cargaManual: this.preInstallationData?.chargeManual || false,
        direccionNotificacionComun: this.preInstallationData?.allAddressNotification || false,
        cuentaBancariaComun: this.isUserSpanish ? (this.preInstallationData?.allAccountBank || false)
          : (this.signerData?.sameBankAccount === '0' || false),
        personaContactoComun: this.preInstallationData?.allContactPerson || false,
        cuentaBancariaComunInfo: {
          iban: this.installationsData.length > 1 ? (this.installationsData[0]?.ibanCode || null) : null,
          entidad: this.installationsData.length > 1 ? (this.installationsData[0]?.ibanEntity || null) : null,
          sucursal: this.installationsData.length > 1 ? (this.installationsData[0]?.ibanOffice || null) : null,
          dc: this.installationsData.length > 1 ? (this.installationsData[0]?.ibanDC || null) : null,
          cuenta: this.installationsData.length > 1 ? (this.installationsData[0]?.ibanAccount || null) : null,
        },
        direccionNotificacionComunInfo: {
          nombreTipoViaDireccion: this.preInstallationData?.addressNotifRoadType || null,
          nombreDireccion: this.preInstallationData?.addressNotifName || null,
          numeroDireccion: this.preInstallationData?.addressNotifNumber || null,
          portalDireccion: this.preInstallationData?.addressNotifGate || null,
          escaleraDireccion: this.preInstallationData?.addressNotifStair || null,
          pisoDireccion: this.preInstallationData?.addressNotifFloor || null,
          puertaDireccion: this.preInstallationData?.addressNotifDoor || null,
          codPostalDireccion: this.preInstallationData?.addressNotifPostalCode || null,
          codigoPoblacionDireccion: this.preInstallationData?.addressNotifTown || null,
          codigoProvinciaDireccion: this.preInstallationData?.addressNotifProvince || null
        },
        personaContactoComunInfo: {
          nombrePersonaContacto: this.preInstallationData?.contactName || null,
          apellidosPersonaContacto: this.preInstallationData?.contactSurname || null,
          dniPersonaContacto: this.preInstallationData?.contactDocumentNumber || null,
          telefonoPersonaContacto: this.preInstallationData?.contactEmail || null,
          mailPersonaContacto: this.preInstallationData?.contactMobile || null
        }
      },
      infoInstalaciones: this.newContractPymesMultipointService.mapperSetInstallations(
        !this.preInstallationData?.chargeManual && this.tabIndex === 2 ? this.preInstallationData.installations : this.installationsData
      ),
      infoContrato: this.newContractPymesMultipointService.mapperSignerContract(this.signerData)
    };
  }

  private postInstallations(): void {
    this.newContractPymesMultipointService.getSavedDataMultipoint().pipe(takeUntil(this.destroyed$)).subscribe(
      value => {
        this.destroyed$.next();
        const contract = {
          idBorrador: value.idDraft,
          contactoCRM: value.contactCRM,
          codigoRazonSocial: value.business.socialReason
        };
        this.showProgressInstallations = true;
        from(this.installationsData).pipe(
          concatMap(res =>
            this.newContractPymesMultipointService.postInstallation(
              { ...contract, ...this.newContractPymesMultipointService.mapperPostInstallation(res) }
            ))
        ).pipe(takeUntil(this.destroyed$)).subscribe(
          response => {
            if (response.success) {
              this.installationsData[this.progressInstallations].codInstallation = response.data;
              this.installationsData[this.progressInstallations].registeredCRM = true;
              this.installationsData[this.progressInstallations].codErrorRegisteredCRM = null;
              this.installationsData[this.progressInstallations].descriptionErrorRegisteredCRM = null;
            } else {
              this.installationsData[this.progressInstallations].codInstallation = null;
              this.installationsData[this.progressInstallations].registeredCRM = false;
              this.installationsData[this.progressInstallations].codErrorRegisteredCRM = response.Info[0].Code;
              this.installationsData[this.progressInstallations].descriptionErrorRegisteredCRM = response.Info[0].Message;
              this.utilService.toastErrors(response);
            }
            this.installationsData[this.progressInstallations].sendedCRM = true;
            this.checkProgressInstallation();
          },
          error => {
            this.progressInstallations = 0;
            this.showProgressInstallations = false;
            this.isLoading = false;
            this.utilService.toastErrors(error);
          }
        );
      });
  }

  private checkProgressInstallation(): void {
    if (this.progressInstallations !== this.installationsData.length) {
      this.progressInstallations++;
    }
    if (this.progressInstallations === this.installationsData.length) {
      this.showProgressInstallations = false;
      this.progressInstallations = 0;
      this.newContractPymesMultipointService.setSavedDataMultipoint({ ...{ installations: this.installationsData } });
      this.checkAllInstallationsApproved();
      this.isLoading = false;
    }
  }

  private checkAllInstallationsApproved(): void {
    if (this.installationsData.every(value => value.registeredCRM)) {
      this.handleSave(4);
    } else if (this.installationsData.some(value => value.registeredCRM)) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        autoFocus: false,
        disableClose: true,
        width: '575px',
        maxHeight: '575px',
        data: this.installationsData
      };
      const dialogRef = this.dialog.open(MultipointModalErrorInstallationComponent, dialogConfig.data);
      dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(result => {
        if (result === 'continue') {
          this.handleSave(4);
        }
      });
    }
  }

  public handleGoNext(): void {
    switch (this.tabIndex) {
      case 1:
        this.postSocialReason();
        break;
      case 2:
        if (this.preInstallationData.chargeManual && this.installationsData.length > 0) {
          this.openModalHaveInstallations();
        } else {
          this.handleSave(3);
        }
        break;
      case 3:
        if (this.isUserSpanish) {
          this.checkIbanRS();
        } else {
          this.checkGeographicZone();
        }
        break;
      case 4:
        if (this.isUserSpanish) {
          this.confirmModal();
        } else {
          if (this.getBodyCheckIbanRS()?.codigosCuentasBancarias[0]?.iban) {
            this.checkIbanRS(true);
          } else {
            this.checkGeographicZone(true);
          }
        }
        break;
    }
  }

  private checkIbanRS(openModal = false): void {
    this.isLoading = true;
    this.newContractPymesMultipointService.postCheckIbanRS(this.getBodyCheckIbanRS()).pipe(takeUntil(this.destroyed$)).subscribe(
      (res: GenericResponse<CheckIbanSRResponse>) => {
        if (res.success) {
          this.isLoading = false;
          if (res.data.contains) {
            this.openModalBankError(this.formatBankAccount(res.data.codigosCuentasBancarias), openModal);
          } else {
            this.checkGeographicZone(openModal);
          }
        } else {
          this.isLoading = false;
          this.utilService.toastErrors(res);
        }
      },
      error => {
        this.isLoading = false;
        this.utilService.toastErrors(error);
      });
  }

  private getBodyCheckIbanRS(): CheckIbanSRRequest {
    return {
      codRazonSocial: this.businessData?.socialReason,
      codigosCuentasBancarias: this.installationsData.map(installation => {
        return {
          iban: installation.ibanCode,
          entidad: installation.ibanEntity,
          sucursal: installation.ibanOffice,
          dc: installation.ibanDC,
          cuenta: installation.ibanAccount
        };
      })
    };
  }

  private openModalBankError(accounts: string[], openModal = false): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      disableClose: true,
      autoFocus: false,
      width: '575px',
      data: {
        multipoint: true,
        accounts
      }
    };
    const dialogRef = this.dialog.open(ModalErrorBankAccountComponent, dialogConfig.data);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.checkGeographicZone(openModal);
      }
    });
  }

  private formatBankAccount(listBankAccount: BankAccount[]): string[] {
    return listBankAccount.map(objAccount => {
      return objAccount.iban + objAccount.entidad + objAccount.sucursal + objAccount.dc + objAccount.cuenta;
    });
  }

  public tabChange(event): void {
    if (this.tabIndex !== event.index + 1) {
      this.tabIndex = event.index + 1;
    }
    switch (this.tabIndex) {
      case 1:
        this.preInstallationData.installations = [];
        this.progress = 0;
        break;
      case 2:
        this.progress = 25;
        break;
      case 3:
        this.preInstallationData.installations = [];
        this.progress = 50;
        break;
      case 4:
        this.preInstallationData.installations = [];
        this.progress = 75;
        break;
      case 5:
        this.progress = 100;
        break;
    }
  }

  private postSocialReason(): void {
    this.isLoading = true;
    this.newContractPymesMultipointService.getSavedDataMultipoint().pipe(takeUntil(this.destroyed$)).subscribe(
      response => {
        this.destroyed$.next();
        const params: SocialReasonMultiPoint = {
          idBorrador: Number(response.idDraft),
          contactoCRM: response.contactCRM,
          nombre: this.businessData.socialReasonLabel,
          cif: this.businessData.numberDocument,
          cnae: this.businessData.cnae,
          nombreContactoComercial: this.businessData.contactName,
          apellidosContactoComercial: this.businessData.surname,
          dniContactoComercial: this.businessData.dni,
          telefonoContactoComercial: this.businessData.mobile,
          mailContactoComercial: this.businessData.email,
          nombreTipoViaDireccion: this.businessData.roadType,
          nombreDireccion: this.businessData.roadName,
          numeroDireccion: this.businessData.addressNumber || null,
          portalDireccion: this.businessData.gate || null,
          escaleraDireccion: this.businessData.stair || null,
          pisoDireccion: this.businessData.floor || null,
          puertaDireccion: this.businessData.door || null,
          codPostalDireccion: this.businessData.postalCode,
          codigoPoblacionDireccion: this.businessData.town,
          codigoProvinciaDireccion: this.businessData.province
        };
        this.newContractPymesMultipointService.socialReasonMultiPoint(params).pipe(takeUntil(this.destroyed$)).subscribe(
          res => {
            if (res.success) {
              this.businessData.socialReason = res.data;
              this.newContractPymesMultipointService.setSavedDataMultipoint({ business: this.businessData });
              this.tabIndex = 2;
            } else {
              this.utilService.toastErrors(res);
            }
            this.isLoading = false;
          },
          error => {
            this.isLoading = false;
            this.utilService.toastErrors(error);
          }
        );
      }
    );
  }

  private checkGeographicZone(openModal = false): void {
    this.handleSave(null, true, true, openModal);
  }

  public handleDataBusiness(event: BusinessMultiPoint): void {
    this.canGoNext = event.canGoNext;
    this.canSave = event.canSave;
    this.businessData = event;
  }

  public handleDataPreInstallation(event: PreviousInstallation): void {
    this.canGoNext = event.canGoNext;
    this.preInstallationData = event;
    if (!this.preInstallationData.chargeManual && this.preInstallationData.installations.length > 0) {
      if (this.installationsData.length > 0) {
        this.openModalHaveInstallations();
      } else {
        this.overWriteInstallations = true;
        this.handleSave(3);
      }
    }
  }

  private openModalHaveInstallations(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      disableClose: true,
      width: '575px'
    };
    const dialogRef = this.dialog.open(MultipointModalHaveInstallationsComponent, dialogConfig.data);
    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(overwrite => {
      if (overwrite) {
        this.overWriteInstallations = true;
        this.installationsData = [];
        this.newContractPymesMultipointService.setSavedDataMultipoint({ installations: this.installationsData });
        this.handleSave(3);
      } else {
        this.overWriteInstallations = false;
        this.handleSave(3);
      }
    });
  }

  public handleDataInstallation(event: InstallationsMultiPoint): void {
    if (event.canGoNext !== undefined) {
      this.canGoNext = event.canGoNext;
    }
    this.installationsData = event.installations;
  }

  public handleDataSigner(event: SignerMultiPoint): void {
    this.canGoNext = event.canGoNext;
    this.signerData = event;
  }

  private confirmModal(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      height: '250px',
      width: '575px',
      disableClose: true
    };
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig.data);
    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(result => {
      if (result === 'yes') {
        this.createContract();
      }
    });
  }

  private createContract(): void {
    this.isLoading = true;
    this.newContractPymesMultipointService.getSavedDataMultipoint().pipe(takeUntil(this.destroyed$)).subscribe(
      value => {
        this.destroyed$.next();
        const params: CreateContractMultipoint = {
          idBorrador: value.idDraft,
          contactoCRM: value.contactCRM,
          enterMargins: this.getEnterMargins(),
          ...this.newContractPymesMultipointService.mapperSignerContract(this.signerData)
        };
        this.newContractPymesMultipointService.createContractMultipoint(params).pipe(takeUntil(this.destroyed$)).subscribe(
          response => {
            if (response.success) {
              this.toastrService.error('NEW_CONTRACTS.CREATED_CONTRACT');
              this.headerService.setNotification(true);
              this.handleGoBack();
            } else {
              this.utilService.toastErrors(response);
            }
            this.isLoading = false;
          }, error => {
            this.isLoading = false;
            this.utilService.toastErrors(error);
          }
        );
      });
  }

  private getEnterMargins(): MultipointEnterMargins[] {
    const enterMargins = this.installationsData.map((installation) => {
      return {
        tarifa: installation.fee,
        enterMargins: {
          marginTP: installation.enterMargins?.marginTP,
          marginsTE: {
            p1: installation.enterMargins?.marginsTE?.p1,
            p2: installation.enterMargins?.marginsTE?.p2,
            p3: installation.enterMargins?.marginsTE?.p3,
            p4: installation.enterMargins?.marginsTE?.p4,
            p5: installation.enterMargins?.marginsTE?.p5,
            p6: installation.enterMargins?.marginsTE?.p6
          }
        }
      };
    }).filter((value, index, self) =>
      index === self.findIndex((item) => (
        item.tarifa === value.tarifa
      ))
    );
    return enterMargins;
  }

  public deleteContract(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      height: '250px',
      width: '575px',
      disableClose: true
    };
    const dialogRef = this.dialog.open(ModalCancelContractComponent, dialogConfig.data);
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.isLoading = true;
        if (this.tabIndex === 5) {
          this.cancelContractService.cancelContract(
            this.newContractPymesMultipointService.getSavedDataMultipoint().value.idContract
          ).pipe(takeUntil(this.destroyed$)).subscribe(
            (response: GenericResponse) => {
              this.handleResponse(response, 'NEW_CONTRACTS.SUCCESS_CANCEL');
            },
            (error: HttpErrorResponse) => {
              this.handleError(error);
            }
          );
        } else {
          this.cancelContractService.cancelDraft(
            this.newContractPymesMultipointService.getSavedDataMultipoint().value.idDraft
          ).pipe(takeUntil(this.destroyed$)).subscribe(
            (response: GenericResponse) => {
              this.handleResponse(response, 'NEW_CONTRACTS.SUCCESS_CANCEL_DRAFT');
            },
            (error: HttpErrorResponse) => {
              this.handleError(error);
            }
          );
        }
      }
    });
  }

  private handleResponse(response: GenericResponse, message: string): void {
    if (response.success) {
      this.toastrService.success(message);
    } else {
      this.utilService.toastErrors(response);
    }
    this.isLoading = false;
    this.handleGoBack();
  }

  private handleError(error: HttpErrorResponse): void {
    this.utilService.toastErrors(error);
    this.isLoading = false;
  }
}
